import React, { useState, useEffect } from 'react';
import { ChevronLeft, Loader2, Crown, Medal, Users, Lock, ShieldCheck, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';

const RANK_STYLES = {
  1: 'from-yellow-400 to-amber-600 text-black',
  2: 'from-zinc-300 to-zinc-500 text-black',
  3: 'from-orange-400 to-amber-700 text-black',
};

/** Classement AMRAP communautaire — opt-in (vie privée), hebdomadaire (reset chaque lundi)
 * ou meilleur score all-time. */
export const CardioLeaderboard = ({ en, onBack }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [period, setPeriod] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [myEntry, setMyEntry] = useState(null);
  const [optin, setOptin] = useState(false);
  const [optinBusy, setOptinBusy] = useState(false);

  const load = (p) => {
    setLoading(true);
    Promise.all([
      authFetch(`/api/tracking/cardio-pro/leaderboard?period=${p}`).then((r) => r.json()),
      authFetch('/api/tracking/cardio-pro/leaderboard/settings').then((r) => r.json()),
    ]).then(([lb, settings]) => {
      setEntries(lb.leaderboard || []);
      setMyEntry(lb.my_entry || null);
      setOptin(!!settings.optin);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(period); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOptin = () => {
    setOptinBusy(true);
    const next = !optin;
    authFetch('/api/tracking/cardio-pro/leaderboard/settings', { method: 'PUT', body: JSON.stringify({ optin: next }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { setOptin(next); toast.success(next ? L('Vous apparaissez maintenant dans le classement !', 'You now appear in the leaderboard!') : L('Vous avez quitté le classement.', 'You left the leaderboard.')); load(period); })
      .catch(() => toast.error(L('Erreur.', 'Error.')))
      .finally(() => setOptinBusy(false));
  };

  const shareRank = () => {
    if (!myEntry) return;
    const text = L(
      `Je suis classé #${myEntry.rank} avec ${myEntry.best_rounds} tours en AMRAP sur Body-Up 💪 À toi de me battre !`,
      `I'm ranked #${myEntry.rank} with ${myEntry.best_rounds} AMRAP rounds on Body-Up 💪 Come beat me!`,
    );
    if (navigator.share) {
      navigator.share({ title: 'Body-Up — Classement AMRAP', text, url: 'https://body-up.ch' }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success(L('Texte copié !', 'Text copied!')));
    }
  };

  return (
    <div className="space-y-5" data-testid="cardio-leaderboard">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="cardio-leaderboard-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <h2 className="text-xl font-black text-white flex items-center gap-2"><Crown className="w-5 h-5 text-yellow-400" /> {L('Classement AMRAP', 'AMRAP Leaderboard')}</h2>

      {myEntry && (
        <button onClick={shareRank}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-[1.02] transition-transform"
          data-testid="cardio-leaderboard-share-btn">
          <Share2 className="w-4 h-4" /> {L(`Partager mon rang #${myEntry.rank}`, `Share my rank #${myEntry.rank}`)}
        </button>
      )}

      <div className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 flex items-center justify-between gap-3" data-testid="cardio-leaderboard-optin-card">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold">{L('Apparaître dans le classement', 'Appear in the leaderboard')}</p>
            <p className="text-xs text-zinc-500">{L('Optionnel — seul votre prénom sera visible des autres membres.', 'Optional — only your first name will be visible to other members.')}</p>
          </div>
        </div>
        <button onClick={toggleOptin} disabled={optinBusy}
          className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${optin ? 'bg-orange-500' : 'bg-zinc-700'}`}
          data-testid="cardio-leaderboard-optin-toggle">
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${optin ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {[{ v: 'weekly', label: L('Cette semaine', 'This week') }, { v: 'alltime', label: L('Meilleur score all-time', 'All-time best') }].map((t) => (
          <button key={t.v} onClick={() => setPeriod(t.v)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${period === t.v ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' : 'bg-zinc-800/60 text-zinc-400'}`}
            data-testid={`cardio-leaderboard-tab-${t.v}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
      ) : entries.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.035] border border-white/10 p-8 text-center text-zinc-500 text-sm" data-testid="cardio-leaderboard-empty">
          <Users className="w-8 h-8 mx-auto mb-3 text-zinc-600" />
          {L("Aucun score AMRAP enregistré pour l'instant. Faites une séance AMRAP pour lancer le classement !", 'No AMRAP score yet. Complete an AMRAP session to kick off the leaderboard!')}
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <div key={e.rank} className={`flex items-center gap-3 rounded-xl p-3 border ${e.is_me ? 'bg-orange-500/10 border-orange-500/30' : 'bg-white/[0.03] border-white/10'}`} data-testid={`cardio-leaderboard-row-${e.rank}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm shrink-0 ${RANK_STYLES[e.rank] ? `bg-gradient-to-br ${RANK_STYLES[e.rank]}` : 'bg-zinc-800 text-zinc-400'}`}>
                {e.rank <= 3 ? <Medal className="w-4 h-4" /> : e.rank}
              </div>
              <p className="flex-1 text-white font-semibold text-sm truncate">{e.display_name}{e.is_me && <span className="text-orange-400"> · {L('Vous', 'You')}</span>}</p>
              <p className="text-orange-300 font-black text-sm shrink-0">{e.best_rounds} {L('tours', 'rounds')}</p>
            </div>
          ))}
        </div>
      )}

      {!optin && (
        <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="cardio-leaderboard-locked-hint">
          <Lock className="w-3.5 h-3.5 shrink-0" /> {L("Activez l'option ci-dessus pour apparaître dans ce classement.", 'Enable the option above to appear in this leaderboard.')}
        </div>
      )}
    </div>
  );
};

export default CardioLeaderboard;
